/**
 * transformers-cache
 *
 * Runtime cache configuration for local transformer models.
 *
 * Primary responsibilities:
 * - Keep model caches on a writable serverless path
 * - Apply cache paths to process env and Xenova runtime env
 *
 * Dependencies:
 * - @xenova/transformers runtime shape
 */

type TransformersRuntimeEnv = {
  allowLocalModels?: boolean
  cacheDir?: string
  useBrowserCache?: boolean
}

type TransformersRuntime = {
  env?: TransformersRuntimeEnv
}

const DEFAULT_CACHE_ROOT = '/tmp'
const CACHE_FOLDER = 'transformers_cache'

const normalizePath = (path: string) => path.replace(/\/{2,}/g, '/')

const isWritableServerlessPath = (path: string) => {
  const normalized = normalizePath(path.trim())

  return normalized.length > 0 && !normalized.startsWith('/vercel')
}

const getWritableRoot = () => {
  const roots = [process.env.TMPDIR, process.env.TMP, DEFAULT_CACHE_ROOT]

  return (
    roots.find((root): root is string =>
      Boolean(root && isWritableServerlessPath(root))
    ) ?? DEFAULT_CACHE_ROOT
  )
}

export const getTransformersCacheDir = () => {
  const configuredCacheDir =
    process.env.LOSI_TRANSFORMERS_CACHE_DIR || process.env.TRANSFORMERS_CACHE

  if (configuredCacheDir && isWritableServerlessPath(configuredCacheDir)) {
    return normalizePath(configuredCacheDir)
  }

  return normalizePath(`${getWritableRoot().replace(/\/$/, '')}/${CACHE_FOLDER}`)
}

export const configureTransformersProcessCacheEnv = () => {
  const cacheDir = getTransformersCacheDir()
  const cacheRoot = cacheDir.replace(new RegExp(`/${CACHE_FOLDER}$`), '')

  process.env.TRANSFORMERS_CACHE = cacheDir
  process.env.HF_HOME = cacheDir
  process.env.HUGGINGFACE_HUB_CACHE = cacheDir
  process.env.XDG_CACHE_HOME = cacheRoot
  process.env.XENOVA_CACHE_DIR = cacheDir

  return cacheDir
}

export const configureTransformersRuntimeCache = (
  transformers: TransformersRuntime
) => {
  const cacheDir = configureTransformersProcessCacheEnv()

  if (transformers.env) {
    transformers.env.cacheDir = cacheDir
    transformers.env.allowLocalModels = false
    transformers.env.useBrowserCache = false
  }

  return cacheDir
}
